/**
 * Back-office bindings for the generic pre-token engine.
 *
 * A pre-token is the short-lived, opaque handle that lets a sensitive,
 * multi-step flow be *resumed* exactly where the user left off by
 * reopening `/<flow>?token=…`. It is NOT an auth/session token — it only
 * carries the current step + non-secret progress.
 *
 * Every request is tagged with a stable, per-browser device id
 * (`X-Device-Id`) so a leaked resume URL can't be continued on another
 * machine — the server binds the token to the issuing IP + device.
 *
 * SECURITY: never put a mnemonic, secret key or passphrase in `data`. The
 * server stores `data` verbatim in Redis; treat it as non-secret.
 *
 * Mirrors `client/src/api/preToken.ts`; kept thin on the back-office
 * `api()` helper.
 */

import { api, ApiError } from "./staff";

export type PreTokenFlow =
  | "signup"
  | "user_login"
  | "staff_onboarding"
  | "staff_login";

const DEVICE_ID_KEY = "dr_device_id";

/** Lazily mint + persist a per-browser id. Falls back to an ephemeral id
 *  when storage is unavailable (private mode) — binding still works for
 *  the lifetime of the tab. */
export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return "ephemeral-" + Math.random().toString(36).slice(2);
  }
}

function deviceHeaders(): Record<string, string> {
  return { "X-Device-Id": getDeviceId() };
}

export interface PreTokenView {
  flow: PreTokenFlow;
  step: string;
  data: Record<string, unknown>;
  userId: string | null;
  expiresInSeconds: number;
}

export interface StartedPreToken {
  token: string;
  flow: PreTokenFlow;
  step: string;
  data: Record<string, unknown>;
  expiresInSeconds: number;
}

export function startPreToken(
  flow: PreTokenFlow,
  init: { step?: string; data?: Record<string, unknown> } = {},
): Promise<StartedPreToken> {
  return api<StartedPreToken>("/pre-token/start", {
    method: "POST",
    headers: deviceHeaders(),
    body: JSON.stringify({ flow, ...init }),
  });
}

/** Resume a flow from a URL token. Resolves to null when the token is
 *  gone/expired (404) so callers can cleanly start fresh. Binding
 *  mismatches still throw. */
export async function resumePreToken(
  token: string,
): Promise<PreTokenView | null> {
  try {
    return await api<PreTokenView>(
      `/pre-token/resume?token=${encodeURIComponent(token)}`,
      { headers: deviceHeaders() },
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export function savePreTokenStep(
  token: string,
  flow: PreTokenFlow,
  patch: { step?: string; data?: Record<string, unknown>; replaceData?: boolean },
): Promise<PreTokenView> {
  return api<PreTokenView>("/pre-token/step", {
    method: "POST",
    headers: deviceHeaders(),
    body: JSON.stringify({ token, flow, ...patch }),
  });
}

export function revokePreToken(token: string): Promise<void> {
  return api<void>("/pre-token/revoke", {
    method: "POST",
    headers: deviceHeaders(),
    body: JSON.stringify({ token }),
  });
}
